import type { ChatBroadcastMsg } from "@hupper/shared/protocol";
import type { GameClient } from "../net/GameClient";

/**
 * ChatBox — CS:GO-style in-match text chat, bottom-left above the vitals.
 *
 * T opens the input, Enter sends, Esc cancels. While the input is up the
 * body carries `chat-open`: FirstPersonController / CrosshairSettings gate
 * their keydown handlers on it (mouse aim + fire stay live, exactly like
 * CS:GO chat), and `onOpen` releases any movement key held across the
 * open so it can't stick. Lines come back through the server broadcast —
 * own ones included — so what you see is always the censored version.
 *
 * `?chat=1` previews the UI without a server: seeded lines + local echo.
 */
export class ChatBox {
  private readonly client: GameClient;
  private readonly onOpen: () => void;
  private readonly root: HTMLDivElement;
  private readonly log: HTMLDivElement;
  private readonly inputRow: HTMLDivElement;
  private readonly input: HTMLInputElement;
  private readonly unsubs: Array<() => void> = [];
  private open = false;
  private previewMode = false;

  /** Max lines on screen. Older ones drop off. */
  private static readonly MAX_LINES = 8;
  /** Line lifetime (ms) before fading out. */
  private static readonly LIFETIME_MS = 10000;
  /** Final fade-out animation (ms). */
  private static readonly FADE_MS = 600;
  /** Server clamps to 120 — mirror it so typing can't outrun the cap. */
  private static readonly MAX_CHARS = 120;

  constructor(client: GameClient, onOpen: () => void) {
    this.client = client;
    this.onOpen = onOpen;

    this.root = document.createElement("div");
    this.root.id = "chat-box";
    this.log = document.createElement("div");
    this.log.className = "chat-log";
    this.inputRow = document.createElement("div");
    this.inputRow.className = "chat-input-row";
    this.inputRow.innerHTML = `<span class="chat-say">SAY</span>`;
    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.maxLength = ChatBox.MAX_CHARS;
    this.input.placeholder = "Type a message…";
    this.input.spellcheck = false;
    this.input.autocomplete = "off";
    this.inputRow.appendChild(this.input);
    this.root.appendChild(this.log);
    this.root.appendChild(this.inputRow);
    document.body.appendChild(this.root);
    this.injectStyles();

    this.unsubs.push(this.client.onChat((msg) => this.append(msg)));

    // T opens the input. Capture-phase like the game handlers so a menu
    // can't swallow it first; ignored while any text field has focus.
    const onGlobalKey = (e: KeyboardEvent) => {
      if (e.code !== "KeyT" || e.repeat) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (this.open) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault(); // keep the "t" out of the freshly focused input
      this.openInput();
    };
    window.addEventListener("keydown", onGlobalKey, { capture: true });
    this.unsubs.push(() =>
      window.removeEventListener("keydown", onGlobalKey, { capture: true } as any),
    );

    this.input.addEventListener("keydown", (e) => {
      // Nothing typed here concerns the menus below (they guard on INPUT
      // targets anyway — this is belt and suspenders).
      e.stopPropagation();
      if (e.code === "Tab") {
        e.preventDefault(); // keep focus in the input (Tab is scoreboard)
      } else if (e.code === "Enter" && !e.isComposing) {
        e.preventDefault();
        this.send();
      } else if (e.code === "Escape") {
        // The browser also drops pointer lock on Esc — nothing we can do
        // about that; click re-locks as usual.
        this.closeInput();
      }
    });
    // Focus lost (alt-tab, a click somewhere) — an open-but-unfocused chat
    // would keep eating the keyboard through the body-class gates.
    this.input.addEventListener("blur", () => this.closeInput());
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  /** `?chat=1` — seeded conversation + local echo, no server needed. */
  debugPreview(): void {
    this.previewMode = true;
    const seed: ChatBroadcastMsg[] = [
      { sessionId: "p1", name: "Nova", team: "ct", text: "gl hf" },
      { sessionId: "p2", name: "Kzinho", team: "t", text: "vamo!" },
      { sessionId: "p2", name: "Kzinho", team: "t", text: "que ***** foi essa kkkk" },
      { sessionId: "p1", name: "Nova", team: "ct", text: "press T to chat" },
    ];
    seed.forEach((m, i) => window.setTimeout(() => this.append(m), i * 700));
  }

  private openInput(): void {
    if (this.open) return;
    this.open = true;
    document.body.classList.add("chat-open");
    this.onOpen(); // release held movement keys before the gate starves keyup
    this.inputRow.classList.add("visible");
    this.input.value = "";
    this.input.focus();
  }

  private closeInput(): void {
    if (!this.open) return;
    this.open = false;
    document.body.classList.remove("chat-open");
    this.inputRow.classList.remove("visible");
    this.input.value = "";
    this.input.blur();
  }

  private send(): void {
    const text = this.input.value.trim().slice(0, ChatBox.MAX_CHARS);
    if (text) {
      if (this.previewMode) {
        this.append({ sessionId: "you", name: "You", team: "ct", text });
      } else {
        this.client.sendChat(text);
      }
    }
    this.closeInput();
  }

  private append(msg: ChatBroadcastMsg): void {
    const row = document.createElement("div");
    row.className = "chat-row";
    const team = msg.team === "t" ? "t" : "ct";
    row.innerHTML =
      `<span class="chat-name chat-team-${team}">${escapeHtml(msg.name)}</span>` +
      `<span class="chat-text">${escapeHtml(msg.text)}</span>`;
    this.log.appendChild(row);

    while (this.log.children.length > ChatBox.MAX_LINES) {
      this.log.firstElementChild?.remove();
    }

    window.setTimeout(() => {
      row.classList.add("chat-fade");
      window.setTimeout(() => row.remove(), ChatBox.FADE_MS);
    }, ChatBox.LIFETIME_MS - ChatBox.FADE_MS);
  }

  private injectStyles(): void {
    if (document.getElementById("chat-box-styles")) return;
    const style = document.createElement("style");
    style.id = "chat-box-styles";
    style.textContent = `
      #chat-box {
        position: fixed;
        left: 16px;
        bottom: 120px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        z-index: 10000; /* above the match-end overlay — GGs belong on the end screen */
        pointer-events: none;
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 12px;
        max-width: min(400px, 44vw);
      }
      #chat-box .chat-log {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 3px;
      }
      /* CS:GO-style pure text — no card behind the line; a layered black
         text-shadow keeps it readable over the bright arena instead. */
      #chat-box .chat-row {
        display: flex;
        align-items: baseline;
        gap: 6px;
        font-size: 13px;
        line-height: 1.35;
        text-shadow:
          0 1px 2px rgba(0, 0, 0, 0.95),
          0 0 3px rgba(0, 0, 0, 0.85),
          0 2px 8px rgba(0, 0, 0, 0.45);
        transition: opacity 600ms ease-out;
        animation: chat-line-in 140ms ease-out;
      }
      @keyframes chat-line-in {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      #chat-box .chat-row.chat-fade { opacity: 0; }
      #chat-box .chat-name { font-weight: 700; white-space: nowrap; }
      #chat-box .chat-name::after { content: ":"; color: rgba(255, 255, 255, 0.75); }
      #chat-box .chat-team-ct { color: #5eb8ff; }
      #chat-box .chat-team-t  { color: #ff8a5c; }
      #chat-box .chat-text {
        color: #f4f4f5;
        min-width: 0;
        overflow-wrap: anywhere;
      }
      #chat-box .chat-input-row {
        display: none;
        align-items: center;
        gap: 8px;
        padding: 5px 9px;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: rgba(8, 9, 12, 0.62);
        backdrop-filter: blur(3px);
        pointer-events: auto;
        width: min(400px, 44vw);
        font-size: 13px;
      }
      #chat-box .chat-input-row.visible { display: flex; }
      #chat-box .chat-say {
        color: #7dffb3;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.08em;
      }
      #chat-box input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: #fff;
        font: inherit;
        caret-color: #7dffb3;
      }
      #chat-box input::placeholder { color: rgba(255, 255, 255, 0.35); }
    `;
    document.head.appendChild(style);
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
